import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { SITE_URL } from "@/config";
import { stories } from "@/mocks/stories";
import { collections } from "@/mocks/collections";
import { StoryCardVertical, SectionHead } from "@/components/feature/cards";

function topicCollection(tag: string) {
  return (
    collections.find(
      (c) =>
        c.name.includes(tag) ||
        c.topic.includes(tag) ||
        c.desc.includes(tag)
    ) || collections.find((c) => c.id === "featured")!
  );
}

export default function StoryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const story = stories.find((s) => s.slug === slug);

  if (!story) {
    return (
      <div className="container-x flex flex-col items-center py-24 text-center">
        <p className="font-heading text-5xl font-black text-primary-500">404</p>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          这块瓜还没找到，可能还没上架
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          它可能已被撤下或地址有误，回首页再逛逛吧。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
        >
          <i className="ri-home-4-line" /> 返回首页
        </Link>
      </div>
    );
  }

  const related = stories
    .filter(
      (s) => s.slug !== story.slug && s.collectionIds.some((c) => story.collectionIds.includes(c))
    )
    .concat(stories.filter((s) => s.slug !== story.slug && !s.collectionIds.some((c) => story.collectionIds.includes(c))))
    .slice(0, 3);

  const breadcrumbList = [
    { name: "首页", path: "/" },
    { name: story.tag, path: `/collection/featured` },
    { name: story.title, path: `/story/${story.slug}` },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbList.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${SITE_URL}${b.path === "/" ? "" : b.path}`,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: story.title,
      description: story.lead,
      image: story.cover,
      datePublished: story.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: "吃瓜日报" },
      publisher: {
        "@type": "Organization",
        name: "吃瓜日报",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/vite.svg` },
      },
      mainEntityOfPage: `${SITE_URL}/story/${story.slug}`,
    },
  ];

  return (
    <>
      <Seo
        title={`${story.title} - 实时吃瓜`}
        description={story.lead}
        keywords={`实时吃瓜,${story.tags.join(",")},${story.tag}`}
        canonicalPath={`/story/${story.slug}`}
        ogImage={story.cover}
        ogType="article"
        jsonLd={jsonLd}
      />

      <article className="bg-gradient-to-b from-background-100/70 to-background-50">
        {/* Breadcrumb */}
        <nav className="container-x pt-6" aria-label="面包屑">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            {breadcrumbList.map((b, i) => (
              <li key={b.path} className="flex items-center gap-1.5">
                {i > 0 ? <i className="ri-arrow-right-s-line text-foreground-300" /> : null}
                {i === breadcrumbList.length - 1 ? (
                  <span className="line-clamp-1 text-foreground-700">{b.name}</span>
                ) : (
                  <Link to={b.path} className="transition-colors hover:text-primary-600">
                    {b.name}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <div className="container-x max-w-4xl py-8 md:py-10">
          {/* Header */}
          <header>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 font-bold text-background-50">
                <i className="ri-fire-fill" /> {story.kind}
              </span>
              <span className="rounded-full bg-secondary-200 px-3 py-1 font-bold text-secondary-900">
                {story.tag}
              </span>
              <span className="text-foreground-500">{story.date}</span>
            </div>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl md:leading-tight">
              {story.title}
            </h1>
            <p className="mt-4 border-l-2 border-primary-400 pl-4 text-base leading-relaxed text-foreground-700 md:text-lg">
              {story.lead}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-5 text-sm text-foreground-500">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500/15 text-accent-700">
                  <i className="ri-quill-pen-line" />
                </span>
                <span>
                  <span className="block font-bold text-foreground-900">{story.author}</span>
                  <span className="text-xs">吃瓜日报编辑</span>
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-fire-fill text-primary-500" /> 热度 {story.heat}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-time-line" /> 阅读约 {story.readTime}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line" /> 编辑推荐
                </span>
              </div>
            </div>
          </header>

          {/* Cover */}
          <div className="relative mt-6 aspect-[21/10] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img src={story.cover} alt={story.coverAlt} className="h-full w-full object-cover" />
          </div>

          {/* Body */}
          <div className="mt-8">
            {story.body.map((b, i) => (
              <div key={i} className="mb-6">
                {b.h ? (
                  <h2 className="mb-3 font-heading text-xl font-bold text-foreground-950">
                    {b.h}
                  </h2>
                ) : null}
                <p className="text-[15px] leading-[1.9] text-foreground-800 md:text-base md:leading-8">
                  {b.p}
                </p>
              </div>
            ))}
          </div>

          {/* Tags */}
          <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
            <span className="mr-1 text-xs font-bold uppercase tracking-[0.18em] text-foreground-500">
              相关专题
            </span>
            {story.tags.map((t) => (
              <Link
                key={t}
                to={`/collection/${topicCollection(t).id}`}
                className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-price-tag-3-line" /> {t}
              </Link>
            ))}
          </div>

          {/* Follow CTA */}
          <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-lg border border-accent-300 bg-accent-100/40 p-5 sm:flex-row sm:items-center md:p-6">
            <div>
              <p className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-spy-line text-accent-700" /> 还想蹲后续？
              </p>
              <p className="mt-1 text-sm text-foreground-700">
                订阅吃瓜日报周报，最新进展与实时吃瓜合集每周准时送达。
              </p>
            </div>
            <a
              href="#subscribe"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-background-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-mail-add-line" /> 订阅后续更新
            </a>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="container-x py-10 md:py-14">
        <SectionHead kicker="继续吃瓜 · 推荐阅读" title="你可能还想看" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((s) => (
            <StoryCardVertical key={s.slug} story={s} />
          ))}
        </div>
      </section>
    </>
  );
}